import React, { useState, useEffect } from 'react';
import { StickyProjectScroll } from './components/StickyProjectScroll';
import './App.css';

// Define project type
interface ProjectType {
  id: number;
  title: string;
  shortDesc: string;
  fullDesc: string;
  image: string;
  icon: string;
  techStack: string[];
  features: string[];
  screenshots: string[];
  github: string;
}

const App: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [typedText, setTypedText] = useState('');
  const fullText = "A developer igniting desktop apps, web platforms, and AI tools. Let's create something epic!";
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Testimonials data
  const testimonials = [
    {
      id: 1,
      name: "Manish",
      role: "CEO, <a href='#' className='hover:underline'>MCStudio</a>",
      quote: "Suhail's leadership at PixelPulse has been transformative. His technical expertise and vision have helped MCStudio collaborate on projects that push boundaries. A true innovator who delivers results consistently."
    },
    {
      id: 2,
      name: "Kedhar",
      role: "CEO, <a href='#' className='hover:underline'>BlazeHub</a>",
      quote: "Working with Suhail has been incredibly valuable for BlazeHub. His deep understanding of both technology and business needs makes him a standout partner. The solutions he builds are elegant and future-proof."
    },
    {
      id: 3,
      name: "Jagadeesh",
      role: "Creator & Maintainer of <a href='#' className='hover:underline'>Diva</a>",
      quote: "Suhail's contributions to the AI space are remarkable. His insights were invaluable during the development of Diva, my personal assistant project. His ability to solve complex problems while keeping the user experience simple is unmatched."
    },
    {
      id: 4,
      name: "Prof. Aman Kumar",
      role: "Assist. Prof, <a href='#' className='hover:underline'>LPU</a>",
      quote: "As an educator, I've seen many talented students, but Suhail stands out for his exceptional blend of technical skill and entrepreneurial mindset. His work ethic and innovative approach to problem-solving make him a true asset to any project."
    },
    {
      id: 5,
      name: "Prof. Ajay Sharma",
      role: "Mentor from <a href='#' className='hover:underline'>upGrad</a>",
      quote: "I've had the pleasure of watching Suhail grow from a promising student to the accomplished CEO he is today. His dedication to continuous learning and his ability to lead teams while staying hands-on with development is impressive. PixelPulse's success is a testament to his vision and execution."
    }
  ];

  // Function to open project detail canvas
  const openProjectDetail = (project: ProjectType) => {
    setSelectedProject(project);
    setIsCanvasOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when canvas is open
  };

  // Function to close project detail canvas
  const closeProjectDetail = () => {
    setIsCanvasOpen(false);
    setTimeout(() => {
      setSelectedProject(null);
    }, 300); // Wait for animation to complete
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  // Project data
  const projects: ProjectType[] = [
    {
      id: 1,
      title: "ParcelPilot",
      shortDesc: "A desktop beast for delivery ops with real-time tracking",
      fullDesc: "A desktop application built with PyQt and PostgreSQL, offering real-time delivery tracking, route optimization, and comprehensive analytics dashboard for logistics companies.",
      image: "/images/projects/parcelpilot-logo.png",
      icon: "fas fa-truck",
      techStack: ["PyQt", "PostgreSQL", "Redis", "Python", "Docker"],
      features: [
        "Real-time delivery tracking and notifications",
        "AI-powered route optimization",
        "Interactive analytics dashboard",
        "Inventory management system",
        "Customer feedback integration"
      ],
      screenshots: [
        "/images/projects/parcelpilot-dashboard.png",
        "/images/projects/parcelpilot-orders.png",
        "/images/projects/parcelpilot-drivers.png"
      ],
      github: "https://github.com/warpirate/delivery-management"
    },
    {
      id: 2,
      title: "BookHUB",
      shortDesc: "An online book haven with top-notch UX",
      fullDesc: "A modern book discovery platform with an intuitive user experience and robust backend, featuring personalized recommendations, social sharing, and seamless e-reader integration.",
      image: "/images/projects/bookhub-logo.png",
      icon: "fas fa-book",
      techStack: ["React", "Node.js", "MongoDB", "Express", "AWS S3"],
      features: [
        "Personalized book recommendations",
        "Social sharing and community features",
        "Integrated e-reader with bookmarking",
        "Author profiles and direct messaging",
        "Reading progress tracking"
      ],
      screenshots: [
        "/images/projects/bookhub-logo.png",
        "/images/projects/bookhub-search.png",
        "/images/projects/bookhub-about.png",
        "/images/projects/bookhub-profile.png"
      ],
      github: "https://github.com/warpirate/bookhub"
    },
    {
      id: 3,
      title: "ScaleMart",
      shortDesc: "An e-commerce powerhouse with live analytics",
      fullDesc: "A comprehensive e-commerce platform with powerful live analytics, inventory management, and customer insights for scaling businesses of all sizes.",
      image: "/images/projects/scalemart-logo.png",
      icon: "fas fa-store",
      techStack: ["Next.js", "TypeScript", "Stripe", "Firebase", "Tailwind CSS"],
      features: [
        "Real-time sales and inventory analytics",
        "Multi-vendor marketplace support",
        "Integrated payment processing",
        "Customer behavior insights",
        "Automated marketing campaigns"
      ],
      screenshots: [
        "/images/projects/scalemart-dashboard.png",
        "/images/projects/scalemart-order.png",
        "/images/projects/scalemart-products.png",
        "/images/projects/scalemart-orders.png",
        "/images/projects/scalemart-users.png"
      ],
      github: "https://github.com/warpirate/scalemart"
    },
    {
      id: 4,
      title: "CosmicFlow",
      shortDesc: "A Harpa-style extension with AI backend",
      fullDesc: "Harpa-style extension with an AI backend that processes and analyzes data flows in real-time, designed for developers who need powerful visualization tools.",
      image: "/images/projects/cosmicflow-logo.png",
      icon: "fas fa-project-diagram",
      techStack: ["JavaScript", "D3.js", "TensorFlow.js", "WebGL", "Chrome Extensions API"],
      features: [
        "Real-time data flow visualization",
        "AI-powered pattern recognition",
        "Interactive node-based editing",
        "Custom visualization templates",
        "Export to multiple formats"
      ],
      screenshots: [],
      github: "https://github.com/warpirate/cosmicflow"
    },
    {
      id: 5,
      title: "P.R.I.S.M.",
      shortDesc: "Personal Response Interface for System Management",
      fullDesc: "P.R.I.S.M. is a sophisticated Python-based AI assistant system designed for intelligent system management. It combines advanced AI capabilities with a modular architecture to provide comprehensive system management solutions.",
      image: "/images/projects/prism-logo.png",
      icon: "fas fa-robot",
      techStack: ["Python", "OpenAI", "SQLite", "Rich"],
      features: [
        "Multi-Agent Architecture",
        "WebSearchAgent: Advanced web-based information retrieval",
        "FileSystemAgent: Comprehensive file system operations",
        "IntentClassificationAgent: Advanced natural language understanding",
        "Persistent Conversation History",
        "Rich Command-Line Interface",
        "Environment-based Configuration",
        "Modular and Extensible Design"
      ],
      screenshots: [
        "/images/projects/prism-main.png"
      ],
      github: "https://github.com/suhailmahamad/prism"
    },
    {
      id: 6,
      title: "Image Generation Tool",
      shortDesc: "A sexy image generator with Amazon Bedrock",
      fullDesc: "A powerful image generation application built on Amazon Bedrock with a highly intuitive user interface. Create stunning visuals with simple text prompts.",
      image: "/images/projects/image-generation-bg.png",
      icon: "fas fa-images",
      techStack: ["React", "AWS Bedrock", "Node.js", "Express", "AWS Lambda"],
      features: [
        "Text-to-image generation",
        "Style transfer and image editing",
        "Batch processing capabilities",
        "Custom style presets",
        "Image history and versioning"
      ],
      screenshots: [
        "/images/projects/image-generation-tool-1.png",
        "/images/projects/image-generation-tool-2.png"
      ],
      github: "https://github.com/warpirate/image-generator"
    },
    {
      id: 7,
      title: "QuantumPDF",
      shortDesc: "A doc analyzer with LLM power",
      fullDesc: "An advanced document analysis tool that uses large language models to extract, summarize, and analyze content from PDF documents with high accuracy.",
      image: "/images/projects/quantumpdf-bg.png",
      icon: "fas fa-file-pdf",
      techStack: ["Python", "PyTorch", "Hugging Face", "FastAPI", "React"],
      features: [
        "PDF text extraction and OCR",
        "Document summarization",
        "Content analysis and insights",
        "Question answering on documents",
        "Multi-language support"
      ],
      screenshots: [],
      github: "https://github.com/warpirate/quantumpdf"
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically handle the form submission
    console.log('Form submitted:', formData);
    setFormData({ name: '', email: '', message: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Preloader effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);
  // Typewriter effect
  useEffect(() => {
    if (isLoading) return;
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypedText(fullText.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [isLoading]);
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  return (
    <div className="min-h-screen bg-[#f5f5f0]">
      {/* Preloader */}
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-[#f5f5f0] z-50">
          <div className="w-16 h-16 border-4 border-[#800000] border-t-[#2f6b2f] rounded-full animate-spin"></div>
        </div>
      )}
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-[#f5f5f0]/90 backdrop-blur-sm shadow-sm z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <h1 className="text-[#800000] font-bold text-xl cursor-pointer">Mahamad Suhail</h1>
            </div>
            {/* Desktop Menu */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                {['Home', 'About', 'Projects', 'Skills', 'Testimonials', 'Contact'].map((item) => (
                  <a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    className="text-[#800000] hover:text-[#2f6b2f] transition-all duration-700 ease-in-out font-medium cursor-pointer !rounded-button whitespace-nowrap transform hover:scale-110"
                  >
                    {item}
                  </a>
                ))}
                
              </div>
            </div>
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-[#800000] hover:text-[#2f6b2f] focus:outline-none cursor-pointer !rounded-button whitespace-nowrap"
              >
                <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Menu */}
        <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} transition-all duration-300 ease-in-out`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-[#f5f5f0] shadow-md">
            {['Home', 'About', 'Projects', 'Skills', 'Testimonials', 'Contact'].map((item) => (
               <a
                 key={item}
                 href={`#${item.toLowerCase()}`}
                 className="block px-3 py-2 text-[#800000] hover:text-[#2f6b2f] transition-all duration-700 ease-in-out font-medium cursor-pointer !rounded-button whitespace-nowrap transform hover:scale-105"
                 onClick={() => setIsMenuOpen(false)}
               >
                 {item}
               </a>
             ))}
             
          </div>
        </div>
      </nav>
      {/* Hero Section */}
      <div
        className="min-h-screen pt-16 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 relative"
        style={{
          backgroundImage: `url('/images/hero-bg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#f5f5f0]/80 backdrop-blur-[2px]"></div>
        <div className="relative z-10 text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-2 text-[#800000]"
              style={{ textShadow: '0 0 10px rgba(47, 107, 47, 0.5)' }}>
            Mahamad Suhail
          </h1>
          <h2 className="text-xl sm:text-2xl font-semibold mb-6 text-[#2f6b2f]">CEO of PixelPulse</h2>
          <div className="mb-2"></div>
          <span className="block sm:inline text-2xl md:text-3xl font-light text-[#800000]">Coding Life into Ideas</span>
          <p className="text-gray-700 text-xl mb-8" style={{ minHeight: '32px' }}>
            {typedText}
            <span className="inline-block w-1 h-6 bg-[#800000] ml-1 animate-pulse"></span>
          </p>
          <a
            href="#projects"
            className="inline-flex h-12 px-8 bg-[#800000] text-white font-medium rounded-md hover:bg-[#2f6b2f] transition-all duration-300 items-center justify-center whitespace-nowrap"
          >
            <span className="flex items-center">Unleash My Work</span>
            <span className="absolute inset-0 bg-white/30 scale-0 rounded-full transform origin-center transition-transform duration-300 ease-out hover:scale-[2.5] opacity-0 hover:opacity-100"></span>
          </a>
        </div>
      </div>
      {/* About Me Section */}
      <div id="about" className="min-h-screen bg-[#f5f5f0] flex items-center justify-center py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Image Container */}
            <div className="w-full md:w-1/2 relative group">
              <div className="relative w-full aspect-[3/4] overflow-hidden rounded-2xl">
                <div
                  className="w-full h-full bg-cover bg-center transform transition-transform duration-500 group-hover:scale-105"
                  style={{
                    backgroundImage: "url('/suhail.jpg')",
                    backgroundAttachment: 'fixed'
                  }}
                ></div>
                <div className="absolute inset-0 border-4 border-[#800000] rounded-2xl opacity-50 glow"></div>
              </div>
            </div>
            {/* Content Container */}
            <div className="w-full md:w-1/2 space-y-6">
              <h2 className="text-4xl font-bold text-[#800000] mb-6">About Me</h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                I'm Mahamad Suhail, CEO of PixelPulse—a tech company I lead—crafting apps and AI tools inspired by nature. With a passion for creating innovative solutions, I bring ideas to life through elegant code and intuitive design.
              </p>
              <div className="flex flex-col space-y-4">
                <div className="flex items-center space-x-4">
                  <i className="fas fa-code text-[#2f6b2f] text-xl w-8"></i>
                  <span className="text-gray-700">Full-stack Development</span>
                </div>
                <div className="flex items-center space-x-4">
                  <i className="fas fa-desktop text-[#2f6b2f] text-xl w-8"></i>
                  <span className="text-gray-700">Desktop Applications</span>
                </div>
                <div className="flex items-center space-x-4">
                  <i className="fas fa-robot text-[#2f6b2f] text-xl w-8"></i>
                  <span className="text-gray-700">AI Integration</span>
                </div>
              </div>
              <div className="flex space-x-4 pt-6">
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="bg-[#800000] text-white px-6 py-3 rounded-md hover:bg-[#2f6b2f] transition-all duration-300 flex items-center space-x-2 !rounded-button whitespace-nowrap">
                  <i className="fab fa-github"></i>
                  <span>GitHub</span>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="bg-[#800000] text-white px-6 py-3 rounded-md hover:bg-[#2f6b2f] transition-all duration-300 flex items-center space-x-2 !rounded-button whitespace-nowrap">
                  <i className="fab fa-linkedin"></i>
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Projects Section */}
      <div id="projects" className="min-h-screen bg-[#f5f5f0] py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#800000] mb-4">My Projects</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">Here are some of my recent projects that showcase my skills and expertise.</p>
          </div>
          {/* PixelPulse Highlight (no card) */}
<div className="mb-12 flex flex-col md:flex-row items-center gap-8">
  <img src="/images/projects/pixelpulse-logo.png" alt="PixelPulse Logo" className="w-20 h-20 object-contain md:mr-6 mb-4 md:mb-0" />
  <div>
    <span className="text-[#800000] font-bold text-lg mr-2">PixelPulse</span>
    <span className="text-[#2f6b2f] font-medium text-base">Founder & CEO</span>
    <p className="text-gray-700 mt-2 mb-1">PixelPulse is my flagship technology company, where I lead a talented team to build innovative digital products and AI-powered solutions. Our mission is to deliver impactful technology that empowers businesses and individuals to thrive in a digital world.</p>
    <a href="#" className="text-[#800000] hover:text-[#2f6b2f] underline font-medium">Visit Website</a>
  </div>
</div>
            <StickyProjectScroll projects={projects} />
        </div>
      </div>
      {/* Skills Section */}
      <div id="skills" className="min-h-screen bg-[#f5f5f0] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#800000] mb-16 text-center">Skills & Expertise</h2>
          
          <div className="mb-20">
            <div className="mb-16">
              <div className="flex items-center mb-8">
                <i className="fas fa-code text-[#800000] text-2xl mr-4"></i>
                <h3 className="text-2xl font-semibold text-[#800000]">Technical Skills</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">Programming Languages</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['JavaScript', 'TypeScript', 'Python', 'C#', 'Java', 'PHP'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">Frontend Technologies</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['React', 'Next.js', 'Vue.js', 'Angular', 'Tailwind CSS', 'SASS'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">Backend Technologies</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['Node.js', 'Express', 'Django', 'Flask', 'ASP.NET', 'Laravel'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">Database Systems</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['MongoDB', 'MySQL', 'PostgreSQL', 'SQLite', 'Firebase', 'Redis'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center mb-8">
                <i className="fas fa-brain text-[#800000] text-2xl mr-4"></i>
                <h3 className="text-2xl font-semibold text-[#800000]">Specialized Skills</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">AI & Machine Learning</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['TensorFlow', 'PyTorch', 'Amazon Bedrock', 'OpenAI API', 'LangChain', 'Hugging Face'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4 border-b border-gray-200 pb-2">Tools & Platforms</h4>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['Git', 'Docker', 'AWS', 'Azure', 'Vercel', 'Netlify'].map((skill, i) => (
                      <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300 cursor-pointer">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Services Section */}
          <div className="mt-24">
            <h2 className="text-4xl font-bold text-[#800000] mb-12 text-center">Services I Offer</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
              <div>
                <div className="flex items-center mb-6">
                  <i className="fas fa-laptop-code text-[#800000] text-2xl mr-4"></i>
                  <h3 className="text-xl font-semibold text-[#800000]">Web Development</h3>
                </div>
                <p className="text-gray-600 mb-6">Creating responsive and dynamic web applications with modern frameworks and best practices for optimal performance.</p>
                <ul className="space-y-2">
                  {['Frontend Development', 'Backend Integration', 'API Development', 'Performance Optimization'].map((feature, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <i className="fas fa-check text-[#2f6b2f] mr-2 mt-1 text-sm"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <div className="flex items-center mb-6">
                  <i className="fas fa-desktop text-[#800000] text-2xl mr-4"></i>
                  <h3 className="text-xl font-semibold text-[#800000]">Desktop Applications</h3>
                </div>
                <p className="text-gray-600 mb-6">Building powerful desktop solutions that enhance productivity and workflow for businesses and individuals.</p>
                <ul className="space-y-2">
                  {['Cross-platform Apps', 'Custom Solutions', 'Legacy System Integration', 'Maintenance & Support'].map((feature, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <i className="fas fa-check text-[#2f6b2f] mr-2 mt-1 text-sm"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <div className="flex items-center mb-6">
                  <i className="fas fa-robot text-[#800000] text-2xl mr-4"></i>
                  <h3 className="text-xl font-semibold text-[#800000]">AI Integration</h3>
                </div>
                <p className="text-gray-600 mb-6">Leveraging artificial intelligence to create smarter, more intuitive tools that solve complex problems.</p>
                <ul className="space-y-2">
                  {['LLM Implementation', 'AI-powered Automation', 'Intelligent Analytics', 'Custom AI Solutions'].map((feature, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <i className="fas fa-check text-[#2f6b2f] mr-2 mt-1 text-sm"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div id="testimonials" className="min-h-screen bg-[#f5f5f0] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#800000] mb-4">What People Say</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">Feedback from colleagues, partners, and mentors who have worked with me.</p>
          </div>
          
          {/* Testimonials Carousel - Minimalist Version */}
          <div className="max-w-4xl mx-auto relative py-6">
            {/* Carousel Container */}
            <div className="overflow-hidden">
              <div 
                className="transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                <div className="flex items-center">
                  {/* Testimonial 1 - Manish */}
                  <div className="w-full flex-shrink-0 px-10 py-8">
                    <div className="relative">
                      
                      <p className="text-gray-700 text-lg mb-4">
                        Suhail's leadership at PixelPulse has been transformative. His technical expertise and vision have helped MCStudio collaborate on projects that push boundaries. A true innovator who delivers results consistently.
                      </p>
                      <div className="flex items-center mt-4">
                        <img src="/images/testimonials/manish.png" alt="Manish" className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                          <h4 className="font-medium text-gray-800">Manish</h4>
                          <p className="text-gray-500 text-sm">CEO, <a href="#" className="hover:underline">MCStudio</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial 2 - Kedhar */}
                  <div className="w-full flex-shrink-0 px-10 py-8">
                    <div className="relative">
                      
                      <p className="text-gray-700 text-lg mb-4">
                        Working with Suhail has been incredibly valuable for BlazeHub. His deep understanding of both technology and business needs makes him a standout partner. The solutions he builds are elegant and future-proof.
                      </p>
                      <div className="flex items-center mt-4">
                        <img src="/images/testimonials/kedhar.jpg" alt="Kedhar" className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                          <h4 className="font-medium text-gray-800">Kedhar</h4>
                          <p className="text-gray-500 text-sm">CEO, <a href="#" className="hover:underline">BlazeHub</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial 3 - Jagadeesh */}
                  <div className="w-full flex-shrink-0 px-10 py-8">
                    <div className="relative">
                      
                      <p className="text-gray-700 text-lg mb-4">
                        Suhail's contributions to the AI space are remarkable. His insights were invaluable during the development of Diva, my personal assistant project. His ability to solve complex problems while keeping the user experience simple is unmatched.
                      </p>
                      <div className="flex items-center mt-4">
                        <img src="/images/testimonials/jagadeesh.jpg" alt="Jagadeesh" className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                          <h4 className="font-medium text-gray-800">Jagadeesh</h4>
                          <p className="text-gray-500 text-sm">Creator & Maintainer of <a href="#" className="hover:underline">Diva</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial 4 - Prof. Aman Kumar */}
                  <div className="w-full flex-shrink-0 px-10 py-8">
                    <div className="relative">
                      
                      <p className="text-gray-700 text-lg mb-4">
                        As an educator, I've seen many talented students, but Suhail stands out for his exceptional blend of technical skill and entrepreneurial mindset. His work ethic and innovative approach to problem-solving make him a true asset to any project.
                      </p>
                      <div className="flex items-center mt-4">
                        <img src="/images/testimonials/aman.jpeg" alt="Prof. Aman Kumar" className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                          <h4 className="font-medium text-gray-800">Prof. Aman Kumar</h4>
                          <p className="text-gray-500 text-sm">Assist. Prof, <a href="#" className="hover:underline">LPU</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Testimonial 5 - Prof. Ajay Sharma */}
                  <div className="w-full flex-shrink-0 px-10 py-8">
                    <div className="relative">
                      
                      <p className="text-gray-700 text-lg mb-4">
                        I've had the pleasure of watching Suhail grow from a promising student to the accomplished CEO he is today. His dedication to continuous learning and his ability to lead teams while staying hands-on with development is impressive. PixelPulse's success is a testament to his vision and execution.
                      </p>
                      <div className="flex items-center mt-4">
                        <img src="/images/testimonials/ajay.jpg" alt="Prof. Ajay Sharma" className="w-10 h-10 rounded-full mr-3 object-cover" />
                        <div>
                          <h4 className="font-medium text-gray-800">Prof. Ajay Sharma</h4>
                          <p className="text-gray-500 text-sm">Mentor from <a href="#" className="hover:underline">upGrad</a></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Navigation Arrows */}
            <button 
              onClick={() => setActiveTestimonial(prev => (prev === 0 ? testimonials.length - 1 : prev - 1))}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-[#800000] hover:text-[#2f6b2f] z-10 focus:outline-none" style={{ transition: 'color 0.5s, transform 0.5s', willChange: 'transform' }}
              aria-label="Previous testimonial"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            
            <button 
              onClick={() => setActiveTestimonial(prev => (prev === testimonials.length - 1 ? 0 : prev + 1))}
              className="absolute right-0 top-1/2 -translate-y-1/2 text-[#800000] hover:text-[#2f6b2f] z-10 focus:outline-none" style={{ transition: 'color 0.5s, transform 0.5s', willChange: 'transform' }}
              aria-label="Next testimonial"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
            
            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`focus:outline-none ${activeTestimonial === index 
                    ? 'bg-[#800000]' 
                    : 'bg-gray-300 hover:bg-gray-400'}`} style={{ transition: 'width 0.5s, background-color 0.5s', width: activeTestimonial === index ? '1rem' : '0.5rem', height: '0.25rem', display: 'inline-block', borderRadius: '0.25rem' }}
                  aria-label={`Go to testimonial ${index + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="min-h-screen bg-[#f5f5f0] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#800000] mb-4">Got a wild project?</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">Let's connect and bring your ideas to life! I'm always excited to discuss new opportunities and collaborations.</p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row gap-16">
              {/* Contact Info */}
              <div className="md:w-2/5">
                <h3 className="text-2xl font-semibold text-[#800000] mb-8 border-b border-gray-200 pb-3">Contact Information</h3>
                
                <div className="space-y-8 mb-12">
                  <div className="flex items-start">
                    <i className="fas fa-envelope text-[#800000] text-xl mt-1 w-8"></i>
                    <div>
                      <h4 className="text-gray-800 font-medium mb-1">Email</h4>
                      <a href="mailto:mahamadsuhail9666@gmail.com" className="text-[#800000] hover:text-[#2f6b2f] transition-colors duration-300">mahamadsuhail9666@gmail.com</a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="fas fa-map-marker-alt text-[#800000] text-xl mt-1 w-8"></i>
                    <div>
                      <h4 className="text-gray-800 font-medium mb-1">Location</h4>
                      <p className="text-gray-600">Khammam, India</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="fas fa-clock text-[#800000] text-xl mt-1 w-8"></i>
                    <div>
                      <h4 className="text-gray-800 font-medium mb-1">Working Hours</h4>
                      <p className="text-gray-600">Monday - Friday: 9AM - 6PM IST</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-gray-800 font-medium mb-4 border-b border-gray-200 pb-2">Connect with me</h4>
                  <div className="flex gap-4">
                    <a
                      href="https://github.com/warpirate"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#333] hover:text-[#800000] transition-colors duration-300 text-2xl"
                      aria-label="GitHub Profile"
                    >
                      <i className="fab fa-github"></i>
                    </a>
                    <a
                      href="https://linkedin.com/in/mahamad-suhail/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0077b5] hover:text-[#800000] transition-colors duration-300 text-2xl"
                      aria-label="LinkedIn Profile"
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                    <a
                      href="https://twitter.com/mahamadsuhail"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1da1f2] hover:text-[#800000] transition-colors duration-300 text-2xl"
                      aria-label="Twitter Profile"
                    >
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a
                      href="https://instagram.com/suhail.md_"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#e1306c] hover:text-[#800000] transition-colors duration-300 text-2xl"
                      aria-label="Instagram Profile"
                    >
                      <i className="fab fa-instagram"></i>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Contact Form */}
              <div className="md:w-3/5">
                <h3 className="text-2xl font-semibold text-[#800000] mb-8 border-b border-gray-200 pb-3">Send Me a Message</h3>
                
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 bg-transparent border-b border-gray-300 focus:border-[#800000] outline-none transition-all duration-300"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 bg-transparent border-b border-gray-300 focus:border-[#800000] outline-none transition-all duration-300"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Your Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Tell me about your project..."
                      rows={6}
                      className="w-full px-4 py-3 bg-transparent border-b border-gray-300 focus:border-[#800000] outline-none transition-all duration-300 resize-none"
                      required
                    ></textarea>
                  </div>
                  
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="privacy" 
                      className="mr-2 h-4 w-4 text-[#800000] focus:ring-[#800000] border-gray-300 rounded"
                      required
                    />
                    <label htmlFor="privacy" className="text-sm text-gray-600">
                      I agree to the <a href="#" className="text-[#800000] hover:text-[#2f6b2f] underline">privacy policy</a>
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    className="px-8 py-3 bg-[#800000] text-white rounded-md hover:bg-[#2f6b2f] transition-all duration-300 inline-flex items-center space-x-2 font-medium"
                  >
                    <i className="fas fa-paper-plane"></i>
                    <span>Send Message</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#800000] text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-center md:text-left"> 2025 Mahamad Suhail. All rights reserved.</p>
            <a
              href="https://drive.google.com/file/d/1vl9E6ep0Fu1niGfRfnDUA-OrgFWOdq2g/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 md:mt-0 px-6 py-2 border border-white rounded-lg hover:bg-white hover:text-[#800000] transition-all duration-300 transform hover:scale-105 !rounded-button whitespace-nowrap"
            >
              Download Resume
            </a>
          </div>
        </div>
      </footer>

      {/* Custom CSS */}
      <style>
        {`
        @keyframes ripple {
          to {
            transform: scale(2.5);
            opacity: 0;
          }
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        .preserve-3d {
          transform-style: preserve-3d;
        }
        .rotate-y-2:hover {
          transform: rotateY(2deg);
        }
        .rotate-x-2:hover {
          transform: rotateX(2deg);
        }
        .glow {
          box-shadow: 0 0 20px rgba(128, 0, 0, 0.3);
          animation: glow 2s infinite alternate;
        }
        @keyframes glow {
          from {
            box-shadow: 0 0 20px rgba(128, 0, 0, 0.3);
          }
          to {
            box-shadow: 0 0 30px rgba(128, 0, 0, 0.5);
          }
        }
        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes orbit {
          from {
            transform: rotate(0deg) translateX(150px) rotate(0deg);
          }
          to {
            transform: rotate(360deg) translateX(150px) rotate(-360deg);
          }
        }
        @media (max-width: 768px) {
          @keyframes orbit {
            from {
              transform: rotate(0deg) translateX(100px) rotate(0deg);
            }
            to {
              transform: rotate(360deg) translateX(100px) rotate(-360deg);
            }
          }
        }
        `}

      </style>
    </div>
  );
};

export default App;